import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { Table } from '@shared/ui/Table';
import { Skeleton } from '@shared/ui/Skeleton';
import {
  RegistryEmpty,
  RegistryFilterSelect,
  RegistryPageBtn,
  RegistryPageButtons,
  RegistryPagination,
} from '@shared/ui/RegistryShell';
import {
  useCreateKadryRequestMutation,
  useListKadryRequestsQuery,
  useListKadryWorkSitesQuery,
} from '@shared/api';
import {
  KADRY_REQUEST_STATUS_LABELS,
  kadryDate,
  kadryRequestJustAdded,
  useKadryAccess,
  useKadryStaff,
  type KadryCreateRequest,
  type KadryRequestStatus,
} from '@entities/kadry';
import {
  KadryCheckbox,
  KadryErrorBanner,
  KadryMuted,
  KadryPageHeader,
  KadryRequestStatusBadge,
  KadryTableScroll,
} from '../ui';
import { RequestFormModal } from './RequestFormModal';

const PAGE_SIZE = 25;

const STATUSES: readonly KadryRequestStatus[] = ['draft', 'active', 'paused', 'closed'];

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(5)}`};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Row = styled.tr`
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }
`;

const TitleCell = styled.td`
  font-weight: 600;
  max-width: 320px;
  overflow-wrap: anywhere;
`;

const Num = styled.td`
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const Nowrap = styled.td`
  white-space: nowrap;
`;

const Gap = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

/** Реестр заявок на подбор (ТЗ §8 п.1) в области пользователя. */
export function RequestsList() {
  const navigate = useNavigate();
  const { can, canWrite } = useKadryAccess();
  const staff = useKadryStaff();
  const [status, setStatus] = useState<KadryRequestStatus | ''>('');
  const [mine, setMine] = useState(false);
  const [page, setPage] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => setPage(0), [status, mine]);

  const query = useMemo(
    () => ({
      status: status || undefined,
      mine: mine || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [status, mine, page],
  );
  const { data, isLoading, isFetching, error, refetch } = useListKadryRequestsQuery(query);
  const { data: sites = [] } = useListKadryWorkSitesQuery({ include_inactive: true });
  const siteName = useMemo(() => new Map(sites.map((s) => [s.id, s.name])), [sites]);

  const [create, createState] = useCreateKadryRequestMutation();

  const items = data ?? [];
  const hasNext = items.length === PAGE_SIZE;
  const filtered = Boolean(status) || mine;

  async function onCreate(body: KadryCreateRequest) {
    try {
      const r = await create(body).unwrap();
      setCreateOpen(false);
      navigate(`/agents/kadry/requests/${r.id}`);
    } catch {
      // Ошибка показывается в форме (createState.error).
    }
  }

  function open(id: string) {
    navigate(`/agents/kadry/requests/${id}`);
  }

  return (
    <>
      <KadryPageHeader
        title="Заявки на подбор"
        subtitle="Заявка описывает, кого и куда нужно найти. Поиск кандидатов запускается только по утверждённой версии профиля поиска."
        actions={
          canWrite('request_create') && (
            <Button type="button" onClick={() => setCreateOpen(true)}>
              + Новая заявка
            </Button>
          )
        }
      />

      <Toolbar>
        <RegistryFilterSelect
          value={status}
          onChange={(e) => setStatus(e.target.value as KadryRequestStatus | '')}
          aria-label="Фильтр по статусу"
        >
          <option value="">Все статусы</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {KADRY_REQUEST_STATUS_LABELS[s]}
            </option>
          ))}
        </RegistryFilterSelect>
        <KadryCheckbox checked={mine} onChange={setMine}>
          Только мои
        </KadryCheckbox>
        {isFetching && !isLoading && <KadryMuted>Обновление…</KadryMuted>}
      </Toolbar>

      {error ? (
        <Gap>
          <KadryErrorBanner error={error} onRefresh={refetch} title="Не удалось загрузить заявки" />
        </Gap>
      ) : null}

      <Card>
        <KadryTableScroll>
          <Table>
            <thead>
              <tr>
                <th>Название</th>
                <th>Объект</th>
                <th>Подразделение</th>
                <th>Статус</th>
                <th>Числ.</th>
                <th>Профиль</th>
                <th>Создана</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    <td>
                      <Skeleton $width="80%" />
                    </td>
                    <td>
                      <Skeleton $width={120} />
                    </td>
                    <td>
                      <Skeleton $width={100} />
                    </td>
                    <td>
                      <Skeleton $width={90} $height={20} $radius="999px" />
                    </td>
                    <td>
                      <Skeleton $width={24} />
                    </td>
                    <td>
                      <Skeleton $width={60} />
                    </td>
                    <td>
                      <Skeleton $width={80} />
                    </td>
                  </tr>
                ))
              ) : items.length === 0 ? (
                <tr>
                  <RegistryEmpty colSpan={7}>
                    {page > 0
                      ? 'На этой странице заявок нет.'
                      : filtered
                        ? 'По выбранным условиям заявок нет. Сбросьте фильтры.'
                        : canWrite('request_create')
                          ? 'Заявок пока нет. Создайте первую — опишите, кого нужно найти.'
                          : 'Заявок в вашей области пока нет.'}
                  </RegistryEmpty>
                </tr>
              ) : (
                items.map((r) => (
                  <Row
                    key={r.id}
                    tabIndex={0}
                    onClick={() => open(r.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') open(r.id);
                    }}
                  >
                    <TitleCell>{r.title}</TitleCell>
                    <td>{r.work_site_id ? (siteName.get(r.work_site_id) ?? '—') : '—'}</td>
                    <td>{r.department ?? '—'}</td>
                    <td>
                      <KadryRequestStatusBadge
                        status={r.status}
                        justAdded={kadryRequestJustAdded(r)}
                      />
                    </td>
                    <Num>{r.headcount}</Num>
                    <Nowrap>
                      {r.active_profile_version != null
                        ? `версия ${r.active_profile_version}`
                        : 'не утверждён'}
                    </Nowrap>
                    <Nowrap>{kadryDate(r.created_at)}</Nowrap>
                  </Row>
                ))
              )}
            </tbody>
          </Table>
        </KadryTableScroll>
      </Card>

      {(page > 0 || hasNext) && (
        <RegistryPagination>
          <span>Страница {page + 1}</span>
          <RegistryPageButtons>
            <RegistryPageBtn
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0 || isFetching}
            >
              ← Назад
            </RegistryPageBtn>
            <RegistryPageBtn
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasNext || isFetching}
            >
              Дальше →
            </RegistryPageBtn>
          </RegistryPageButtons>
        </RegistryPagination>
      )}

      <RequestFormModal
        open={createOpen}
        workSites={sites}
        showBudget={can('admin_settings')}
        fullAccess={can('search_run')}
        requesterChoice={
          staff.available
            ? { options: staff.requesters, loading: staff.isLoading, nameOf: staff.nameOf }
            : null
        }
        busy={createState.isLoading}
        error={createState.error}
        onDismissError={() => createState.reset()}
        onCreate={onCreate}
        onClose={() => {
          setCreateOpen(false);
          createState.reset();
        }}
      />
    </>
  );
}
